import React, { useEffect, useState } from 'react';
import { Modal, Table, message, Spin, Button as AntButton, Tabs as AntTabs } from 'antd';
import { FileTextOutlined, PrinterOutlined, MailOutlined } from '@ant-design/icons';
import { openSettlementPrintPreview, generateSettlementPDFBlob } from '../utils/settlementPdfUtil';
import { getUserCompanyInfo, uploadS3, saveGroupInfoAttachment, getGroupEventInfo } from '../services/apiService';
import { getCompanySmtpSettings, sendEmail } from '../services/smtpService';

/**
 * SettlementModal - Display and export settlement document
 * Shows financial summary and detailed breakdowns for a group
 */
const SettlementModal = ({ open, onClose, settlementData, loading, recipientEmail }) => {
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [smtpSettings, setSmtpSettings] = useState(null);
  const [company, setCompany] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailPreparing, setEmailPreparing] = useState(false);
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState('');

  const fmtMoney = (v) => {
    if (v === null || v === undefined || v === '') return '0';
    const num = Math.trunc(Number(String(v).replace(/,/g, '')));
    if (!Number.isFinite(num)) return String(v);
    return num.toLocaleString('ko-KR');
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const smtp = await getCompanySmtpSettings();
        setSmtpSettings(smtp || null);
      } catch (e) {
        console.warn('SMTP 설정 조회 실패:', e);
      }
      try {
        const info = await getUserCompanyInfo();
        setCompany(info || null);
      } catch (e) {
        console.warn('회사 정보 조회 실패:', e);
      }
    })();
  }, [open]);

  const handlePrintPreview = async () => {
    try {
      await openSettlementPrintPreview(settlementData);
    } catch (error) {
      console.error('Print preview error:', error);
      message.error('인쇄 미리보기 중 오류가 발생했습니다.');
    }
  };

  const getDefaultEmailBody = (pdfUrl, companyName, groupName, eventPeriod) => {
    const escapeHtml = (v) => {
      try {
        return String(v || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      } catch {
        return '';
      }
    };
    const safeUrl = escapeHtml(pdfUrl);
    const safeGroup = escapeHtml(groupName);
    const safePeriod = escapeHtml(eventPeriod);
    const safeCompany = escapeHtml(companyName);

    return `<!DOCTYPE html>
<html>
  <body style="font-family: Pretendard, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Malgun Gothic', Arial, sans-serif; color:#111827; line-height:1.7; font-size:14px; margin:0; padding:0;">
    <div style="max-width:640px; margin:0 auto; padding:16px;">
      <p style="margin:0 0 12px 0;">안녕하세요.</p>
      <p style="margin:0 0 20px 0;">${safeCompany || '저희 여행사'}입니다.<br/>요청하신 정산서를 발송해 드립니다.</p>

      <div style="background:#f9fafb; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin:0 0 16px 0;">
        <div style="margin:0 0 6px 0;"><strong style="color:#374151;">■ 단체명:</strong> ${safeGroup || '-'}</div>
        <div><strong style="color:#374151;">■ 여행기간:</strong> ${safePeriod || '-'}</div>
      </div>

      <p style="margin:0 0 12px 0;">아래 버튼을 클릭하여 정산서를 확인하실 수 있습니다.</p>
      <p style="margin:0 0 20px 0;">
        <a href="${safeUrl || '#'}" target="_blank" style="display:inline-block; background:#2563eb; color:#ffffff; text-decoration:none; padding:10px 16px; border-radius:8px; font-weight:600;">정산서 열기</a>
      </p>

      ${safeUrl ? `<p style="margin:0 0 20px 0; color:#6b7280; font-size:12px;">버튼이 보이지 않으면 아래 링크를 복사하여 브라우저에 붙여넣기 해주세요.<br/><span style="word-break:break-all;">${safeUrl}</span></p>` : ''}

      <p style="margin:0 0 12px 0;">문의사항이 있으시면 언제든지 연락 주시기 바랍니다.</p>
      <p style="margin:0 0 24px 0;">감사합니다.</p>
      <p style="margin:0;">${safeCompany || ''}</p>
    </div>
  </body>
</html>`;
  };

  const resolveDefaultRecipient = () => {
    const fromProp = String(recipientEmail || '').trim();
    if (fromProp) return fromProp;

    const rows = Array.isArray(settlementData?.revenueBreakdown)
      ? settlementData.revenueBreakdown
      : [];
    for (const row of rows) {
      const email = String(row?.주문자이메일 || row?.이메일 || '').trim();
      if (email) return email;
    }
    return '';
  };

  const handlePrepareAndShowEmail = async () => {
    if (emailPreparing || loading) return;

    if (!settlementData) {
      message.error('정산 데이터가 없습니다.');
      return;
    }

    if (!smtpSettings) {
      message.error('SMTP 설정이 없습니다. 설정 > 발신메일 관리에서 SMTP를 설정해주세요.');
      return;
    }

    const saleNo = settlementData?.saleNo;
    const groupNo = settlementData?.groupNo;
    if (!saleNo && !groupNo) {
      message.error('단체번호/판매번호가 없어 PDF를 업로드할 수 없습니다.');
      return;
    }

    try {
      setEmailPreparing(true);
      const hide = message.loading('정산서 PDF 생성 및 업로드 중...', 0);

      const { blob, fileName } = await generateSettlementPDFBlob(settlementData);
      const pdfFile = new File([blob], fileName, { type: 'application/pdf' });

      let groupInfo = null;
      if (saleNo) {
        try {
          groupInfo = await getGroupEventInfo({ saleNo });
        } catch (e) {
          console.warn('단체정보 조회 실패(무시):', e);
        }
      }

      const resolvedGroupNo = groupInfo?.단체번호 || groupNo;
      if (!resolvedGroupNo) {
        hide();
        message.error('단체번호를 찾을 수 없습니다.');
        return;
      }

      const filePath = `1/EventGroup/${resolvedGroupNo}/`;
      const uploadResult = await uploadS3({
        file: pdfFile,
        fileName,
        filePath,
      });

      const finalPdfUrl = uploadResult?.url || '';
      if (!finalPdfUrl) {
        hide();
        message.error('PDF 업로드에 실패했습니다. (응답에 url이 없습니다)');
        return;
      }

      const prefix = 'https://kr.object.iwinv.kr/orgin/';
      const storedPath = finalPdfUrl.startsWith(prefix)
        ? finalPdfUrl.slice(prefix.length)
        : `${filePath}${fileName}`;

      try {
        await saveGroupInfoAttachment({
          groupNo: resolvedGroupNo,
          groupName: groupInfo?.행사단체명 || settlementData?.groupName || '',
          managerNo: groupInfo?.수배담당번호 || '',
          startDate: groupInfo?.시작일 || settlementData?.startDate || '',
          endDate: groupInfo?.종료일 || settlementData?.endDate || '',
          attachFileName: fileName,
          attachFilePath: storedPath,
          remark: groupInfo?.단체비고 || settlementData?.remark || '',
          preDepositClose: false,
          autoJoinClose: '',
          landCompanyNo: '',
        });
      } catch (attachErr) {
        console.warn('단체정보 첨부 저장 실패(무시):', attachErr);
      }

      hide();

      const groupName = settlementData?.groupName || '단체';
      const period =
        settlementData?.startDate && settlementData?.endDate
          ? `${settlementData.startDate} ~ ${settlementData.endDate}`
          : '-';

      setUploadedPdfUrl(finalPdfUrl);
      setEmailRecipient(resolveDefaultRecipient());
      setEmailSubject(`[정산서] ${groupName}`);
      setEmailBody(getDefaultEmailBody(finalPdfUrl, company?.업체명, groupName, period));
      setEmailModalOpen(true);
    } catch (e) {
      console.error('정산서 이메일 준비 실패:', e);
      message.error(`정산서 PDF 생성/업로드에 실패했습니다: ${e?.message || '알 수 없는 오류'}`);
    } finally {
      setEmailPreparing(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailRecipient?.trim()) {
      message.error('수신자 이메일을 입력해주세요.');
      return;
    }
    if (!emailSubject?.trim()) {
      message.error('메일 제목을 입력해주세요.');
      return;
    }
    if (!smtpSettings) {
      message.error('SMTP 설정이 없습니다.');
      return;
    }

    try {
      setSendingEmail(true);
      const hide = message.loading('이메일 발송 중...', 0);

      const recipients = emailRecipient
        .split(/[,;]/)
        .map((e) => e.trim())
        .filter((e) => e.length > 0);

      if (recipients.length === 0) {
        hide();
        message.error('유효한 수신자 이메일이 없습니다.');
        return;
      }

      await sendEmail({
        recipients,
        subject: emailSubject,
        body: emailBody,
        smtpServer: smtpSettings?.서버주소 || '',
        smtpPort: smtpSettings?.포트 || 465,
        smtpUsername: smtpSettings?.메일주소 || '',
        smtpPassword: smtpSettings?.비밀번호 || '',
      });

      hide();
      message.success('이메일이 성공적으로 발송되었습니다.');
      setEmailModalOpen(false);
    } catch (e) {
      console.error('정산서 이메일 발송 실패:', e);
      message.error(`이메일 발송에 실패했습니다: ${e?.message || '알 수 없는 오류'}`);
    } finally {
      setSendingEmail(false);
    }
  };

  const {
    groupName = '-',
    groupNo = '-',
    saleNo = '-',
    startDate = '-',
    endDate = '-',
    managerName = '-',
    revenue = 0,
    receivable = 0,
    preDeposit = 0,
    otherIncome = 0,
    expectedProfit = 0,
    applyVat = false,
    expectedVat = 0,
    afterTaxProfit = 0,
    revenueBreakdown = [],
    receivableBreakdown = [],
    preDepositBreakdown = [],
    otherIncomeBreakdown = [],
    depositBreakdown = [],
    remark = '',
  } = settlementData || {};

  const safeReceivableBreakdown = Array.isArray(receivableBreakdown) ? receivableBreakdown : [];

  // Revenue breakdown table columns
  const revenueColumns = [
    {
      title: '주문번호',
      dataIndex: '주문번호',
      key: '주문번호',
      width: 120,
      align: 'center',
    },
    {
      title: '주문자',
      dataIndex: '주문자',
      key: '주문자',
      width: 140,
      align: 'center',
    },
    {
      title: '상품금액',
      dataIndex: '상품금액',
      key: '상품금액',
      width: 130,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
    {
      title: '추가 판매가',
      dataIndex: '택스및유류',
      key: '택스및유류',
      width: 130,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
    {
      title: '프로모션할인',
      dataIndex: '프로모션할인',
      key: '프로모션할인',
      width: 140,
      align: 'right',
      render: (v) => {
        const num = Math.trunc(Number(String(v ?? '').replace(/,/g, '')));
        if (!Number.isFinite(num)) return <span className="tabular-nums">{String(v ?? '')}</span>;
        const displayAbs = fmtMoney(Math.abs(num));
        const signed = num < 0 ? `-${displayAbs}` : `-${displayAbs}`; // always show as negative visually
        return <span className="tabular-nums text-red-600">{signed}</span>;
      },
    },
    {
      title: '고객결제가',
      dataIndex: '고객결제가',
      key: '고객결제가',
      width: 130,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
  ];

  // Deposit (입금) list table columns
  const depositColumns = [
    {
      title: '수납일',
      dataIndex: '수납일',
      key: '수납일',
      width: 120,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '송금(카드소유)자',
      dataIndex: '송금카드소유자',
      key: '송금카드소유자',
      width: 160,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '결제구분',
      dataIndex: '결제구분',
      key: '결제구분',
      width: 120,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '계좌정보',
      dataIndex: '계좌카드번호',
      key: '계좌카드번호',
      width: 180,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '입금액',
      dataIndex: '입금액',
      key: '입금액',
      width: 140,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
  ];

  // Receivable breakdown table columns
  const receivableColumns = [
    {
      title: '주문번호',
      dataIndex: '주문번호',
      key: '주문번호',
      width: 120,
      align: 'center',
    },
    {
      title: '주문자',
      dataIndex: '주문자',
      key: '주문자',
      width: 140,
      align: 'center',
    },
    {
      title: '고객결제가',
      dataIndex: '고객결제가',
      key: '고객결제가',
      width: 130,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
    {
      title: '입금액',
      dataIndex: '입금액',
      key: '입금액',
      width: 130,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
    {
      title: '미수액',
      dataIndex: '미수액',
      key: '미수액',
      width: 130,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
  ];

  // Pre-deposit breakdown table columns (requested fields)
  const preDepositColumns = [
    {
      title: '출납일',
      dataIndex: '매핑출납일',
      key: '매핑출납일',
      width: 140,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '지급액',
      dataIndex: '지급액',
      key: '지급액',
      width: 150,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
    {
      title: '종류',
      dataIndex: '매핑종류',
      key: '매핑종류',
      width: 120,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '결제구분',
      dataIndex: '매핑결제구분',
      key: '매핑결제구분',
      width: 120,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '송금카드소유자',
      dataIndex: '매핑송금카드소유자',
      key: '매핑송금카드소유자',
      width: 160,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '계좌정보',
      dataIndex: '매핑계좌카드번호',
      key: '매핑계좌카드번호',
      width: 180,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '비고',
      dataIndex: '비고',
      key: '비고',
      width: 220,
      align: 'center',
      render: (v) => v || '-',
    },
  ];

  const otherIncomeColumns = [
    {
      title: '항목',
      dataIndex: '항목',
      key: '항목',
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '담당자',
      dataIndex: '담당자',
      key: '담당자',
      width: 120,
      align: 'center',
      render: (v) => v || '-',
    },
    {
      title: '금액',
      dataIndex: '한화환산',
      key: '한화환산',
      width: 140,
      align: 'right',
      render: (v, row) => <span className="tabular-nums">{fmtMoney(v ?? row?.금액)}</span>,
    },
    {
      title: '입금액',
      dataIndex: '입금액',
      key: '입금액',
      width: 140,
      align: 'right',
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
    },
    {
      title: '비고',
      dataIndex: '비고',
      key: '비고',
      render: (v) => v || '-',
    },
  ];

  // Calculate summary for tables
  const calculateSum = (data, key) => {
    return data.reduce((acc, row) => {
      const raw = String(row?.[key] ?? '');
      const cleaned = raw.replace(/,/g, '').replace(/[^0-9-]/g, '');
      const num = Number(cleaned);
      const val = Number.isFinite(num) ? num : 0;
      return acc + val;
    }, 0);
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        width={1000}
        zIndex={2100}
        getContainer={() => document.body}
        maskClosable={false}
        title={
          <div className="flex items-center gap-2">
            <FileTextOutlined className="text-gray-800" />
            <span className="font-semibold text-gray-900">정산서</span>
          </div>
        }
        footer={
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              발행일: {new Date().toLocaleDateString('ko-KR')}
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="h-9 px-3.5 rounded-lg text-sm font-semibold border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 transition-colors"
              >
                닫기
              </button>
              <button
                onClick={handlePrintPreview}
                disabled={loading || emailPreparing}
                className="h-9 px-3.5 rounded-lg text-sm font-semibold border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <PrinterOutlined />
                인쇄 미리보기
              </button>
              <button
                onClick={handlePrepareAndShowEmail}
                disabled={loading || emailPreparing}
                className="h-9 px-3.5 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <MailOutlined />
                {emailPreparing ? '준비 중...' : '메일 보내기'}
              </button>
            </div>
          </div>
        }
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Spin size="large" tip="정산 정보를 불러오는 중..." />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Document Info */}
            <div className="bg-gray-50 border border-gray-300 rounded p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex">
                  <span className="font-semibold text-gray-700 min-w-[100px]">단체명:</span>
                  <span className="text-gray-900">{groupName}</span>
                </div>
                <div className="flex">
                  <span className="font-semibold text-gray-700 min-w-[100px]">단체번호:</span>
                  <span className="text-gray-900">{groupNo}</span>
                </div>
                <div className="flex">
                  <span className="font-semibold text-gray-700 min-w-[100px]">판매번호:</span>
                  <span className="text-gray-900">{saleNo}</span>
                </div>
                <div className="flex">
                  <span className="font-semibold text-gray-700 min-w-[100px]">담당자:</span>
                  <span className="text-gray-900">{managerName}</span>
                </div>
                <div className="flex col-span-2">
                  <span className="font-semibold text-gray-700 min-w-[100px]">여행기간:</span>
                  <span className="text-gray-900">
                    {startDate && endDate ? `${startDate} ~ ${endDate}` : '-'}
                  </span>
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-3 pb-2 border-b-2 border-gray-400">
                정산 요약
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-gray-300 rounded p-3 bg-white">
                  <div className="text-xs font-semibold text-gray-600 mb-2">행사 매출액</div>
                  <div className="text-xl font-bold text-gray-900 text-right tabular-nums">
                    {fmtMoney(revenue)} 원
                  </div>
                </div>
                <div className="border border-gray-300 rounded p-3 bg-white">
                  <div className="text-xs font-semibold text-gray-600 mb-2">행사 미수금</div>
                  <div className="text-xl font-bold text-gray-900 text-right tabular-nums">
                    {fmtMoney(receivable)} 원
                  </div>
                </div>
                <div className="border border-gray-300 rounded p-3 bg-white">
                  <div className="text-xs font-semibold text-gray-600 mb-2">행사 전도금(지출)</div>
                  <div className="text-xl font-bold text-gray-900 text-right tabular-nums">
                    {fmtMoney(preDeposit)} 원
                  </div>
                </div>
                <div className="border border-gray-300 rounded p-3 bg-white">
                  <div className="text-xs font-semibold text-gray-600 mb-2">기타손익</div>
                  <div className="text-xl font-bold text-gray-900 text-right tabular-nums">
                    {fmtMoney(otherIncome)} 원
                  </div>
                </div>
                <div className="border border-gray-300 rounded p-3 bg-white">
                  <div className="text-xs font-semibold text-gray-600 mb-2">행사 예상수익</div>
                  <div className="text-xl font-bold text-gray-900 text-right tabular-nums">
                    {fmtMoney(expectedProfit)} 원
                  </div>
                </div>
                {applyVat && (
                  <>
                    <div className="border border-gray-300 rounded p-3 bg-white">
                      <div className="text-xs font-semibold text-gray-600 mb-2">예상 부가세 (10%)</div>
                      <div className="text-xl font-bold text-gray-900 text-right tabular-nums">
                        {fmtMoney(expectedVat)} 원
                      </div>
                    </div>
                    <div className="border border-emerald-300 rounded p-3 bg-emerald-50">
                      <div className="text-xs font-semibold text-emerald-700 mb-2">세후 예상수익</div>
                      <div className="text-xl font-bold text-emerald-800 text-right tabular-nums">
                        {fmtMoney(afterTaxProfit)} 원
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Revenue Breakdown */}
            {Array.isArray(revenueBreakdown) && revenueBreakdown.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3 py-2 px-3 bg-gray-200 border-l-4 border-gray-800">
                  매출액 상세 내역
                </h3>
                <Table
                  size="small"
                  columns={revenueColumns}
                  dataSource={revenueBreakdown}
                  rowKey={(row, idx) => `revenue-${row?.주문번호 || idx}`}
                  pagination={false}
                  bordered
                  scroll={{ x: 650, y: 300 }}
                  summary={(pageData) => {
                    const total상품 = calculateSum(pageData, '상품금액');
                    const total유류 = calculateSum(pageData, '택스및유류');
                    // For promotion discount, subtract positive values (treat as negative contribution)
                    const total할인Raw = pageData.reduce((acc, row) => {
                      const raw = String(row?.['프로모션할인'] ?? '');
                      const cleaned = raw.replace(/,/g, '').replace(/[^0-9-]/g, '');
                      const num = Number(cleaned);
                      const val = Number.isFinite(num) ? (num > 0 ? -num : num) : 0;
                      return acc + val;
                    }, 0);
                    const total결제 = calculateSum(pageData, '고객결제가');
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={2} align="center">
                            <span className="font-semibold">합계</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="right">
                            <span className="tabular-nums font-semibold">{fmtMoney(total상품)}</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="right">
                            <span className="tabular-nums font-semibold">{fmtMoney(total유류)}</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align="right">
                            <span className="tabular-nums font-semibold text-red-600">{fmtMoney(total할인Raw)}</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align="right">
                            <span className="tabular-nums font-semibold">{fmtMoney(total결제)}</span>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              </div>
            )}

            {/* Deposit List (입금 리스트) */}
            {Array.isArray(depositBreakdown) && depositBreakdown.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3 py-2 px-3 bg-gray-200 border-l-4 border-gray-800">
                  입금 리스트
                </h3>
                <Table
                  size="small"
                  columns={depositColumns}
                  dataSource={depositBreakdown}
                  rowKey={(row, idx) => `deposit-${row?.거래일련번호 || idx}`}
                  pagination={false}
                  bordered
                  scroll={{ x: 600, y: 300 }}
                  summary={(pageData) => {
                    const total입금 = calculateSum(pageData, '입금액');
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={4} align="center">
                            <span className="font-semibold">합계</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align="right">
                            <span className="tabular-nums font-semibold">{fmtMoney(total입금)}</span>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              </div>
            )}

            {/* Receivable Breakdown */}
            {safeReceivableBreakdown.length > 0 ? (
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3 py-2 px-3 bg-gray-200 border-l-4 border-gray-800">
                  미수금 상세 내역
                </h3>
                <Table
                  size="small"
                  columns={receivableColumns}
                  dataSource={safeReceivableBreakdown}
                  rowKey={(row, idx) => `receivable-${row?.주문번호 || idx}`}
                  pagination={false}
                  bordered
                  scroll={{ x: 650, y: 300 }}
                  summary={(pageData) => {
                    const total결제 = calculateSum(pageData, '고객결제가');
                    const total입금 = calculateSum(pageData, '입금액');
                    const total미수 = calculateSum(pageData, '미수액');
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={2} align="center">
                            <span className="font-semibold">합계</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="right">
                            <span className="tabular-nums font-semibold">{fmtMoney(total결제)}</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="right">
                            <span className="tabular-nums font-semibold">{fmtMoney(total입금)}</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align="right">
                            <span className="tabular-nums font-semibold">{fmtMoney(total미수)}</span>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              </div>
            ) : (
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3 py-2 px-3 bg-gray-200 border-l-4 border-gray-800">
                  미수금 상세 내역
                </h3>
                <div className="border border-gray-200 rounded px-3 py-4 bg-gray-50 text-sm text-gray-600">
                  미수금 없음
                </div>
              </div>
            )}

            {/* Pre-Deposit Breakdown */}
            {Array.isArray(preDepositBreakdown) && preDepositBreakdown.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3 py-2 px-3 bg-gray-200 border-l-4 border-gray-800">
                  전도금(지출) 상세 내역
                </h3>
                <Table
                  size="small"
                  columns={preDepositColumns}
                  dataSource={preDepositBreakdown}
                  rowKey={(row, idx) => `predeposit-${row?.세부내역번호 || idx}`}
                  pagination={false}
                  bordered
                  scroll={{ x: 520, y: 300 }}
                  summary={(pageData) => {
                    const total = calculateSum(pageData, '지급액');
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} align="center">
                            <span className="font-semibold">합계</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} />
                          <Table.Summary.Cell index={2} align="right">
                            <span className="tabular-nums font-semibold">{fmtMoney(total)}</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} colSpan={3} />
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              </div>
            )}

            {/* Other Income Breakdown */}
            {Array.isArray(otherIncomeBreakdown) && otherIncomeBreakdown.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3 py-2 px-3 bg-gray-200 border-l-4 border-gray-800">
                  기타손익 상세 내역
                </h3>
                <Table
                  size="small"
                  columns={otherIncomeColumns}
                  dataSource={otherIncomeBreakdown}
                  rowKey={(row, idx) => `otherincome-${row?.기타수익번호 || idx}`}
                  pagination={false}
                  bordered
                  scroll={{ y: 300 }}
                  summary={(pageData) => {
                    const total = pageData.reduce((acc, row) => {
                      const raw = String(row?.한화환산 ?? row?.금액 ?? '');
                      const cleaned = raw.replace(/,/g, '').replace(/[^0-9-]/g, '');
                      const num = Number(cleaned);
                      return acc + (Number.isFinite(num) ? num : 0);
                    }, 0);
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} align="center">
                            <span className="font-semibold">합계</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} />
                          <Table.Summary.Cell index={2} align="right">
                            <span className="tabular-nums font-semibold">{fmtMoney(total)}</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} colSpan={2} />
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              </div>
            )}

            {/* Remark */}
            {remark && String(remark).trim() && (
              <div className="bg-gray-50 border border-gray-300 rounded p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">비고</h3>
                <div className="text-sm text-gray-700 whitespace-pre-wrap">{String(remark)}</div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Email Send Modal — above Drawer(1000) and Settlement(2100) */}
      <Modal
        open={emailModalOpen}
        onCancel={() => setEmailModalOpen(false)}
        footer={null}
        title={
          <div className="flex items-center gap-2">
            <MailOutlined className="text-emerald-600" />
            <span>정산서 이메일 발송</span>
          </div>
        }
        width={640}
        destroyOnClose
        maskClosable={false}
        centered
        zIndex={2300}
        getContainer={() => document.body}
      >
        <div className="space-y-4 pt-2">
          <div className={`p-3 rounded-lg text-sm ${smtpSettings ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
            {smtpSettings ? (
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                SMTP 설정 완료 ({smtpSettings?.서버주소})
              </div>
            ) : (
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                SMTP 설정이 필요합니다. 설정 &gt; 발신메일 관리에서 설정해주세요.
              </div>
            )}
          </div>

          {uploadedPdfUrl ? (
            <div className="p-3 rounded-lg text-sm bg-blue-50 text-blue-700">
              정산서 PDF가 업로드되었습니다.{' '}
              <a href={uploadedPdfUrl} target="_blank" rel="noopener noreferrer" className="underline font-medium">
                미리보기
              </a>
            </div>
          ) : null}

          <div className="space-y-2">
            <label className="block text-[13px] font-semibold text-gray-600">
              수신자 이메일 <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full border border-gray-300 rounded-lg px-2.5 h-9 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm"
              value={emailRecipient}
              onChange={(e) => setEmailRecipient(e.target.value)}
              placeholder="example@email.com (여러 개는 쉼표/세미콜론 구분)"
            />
            <p className="text-xs text-gray-500">여러 수신자는 쉼표(,) 또는 세미콜론(;)으로 구분하세요.</p>
          </div>

          <div className="space-y-2">
            <label className="block text-[13px] font-semibold text-gray-600">
              메일 제목 <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full border border-gray-300 rounded-lg px-2.5 h-9 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="메일 제목을 입력하세요"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-[13px] font-semibold text-gray-600">메일 본문</label>
            <AntTabs
              defaultActiveKey="preview"
              items={[
                {
                  key: 'edit',
                  label: '편집',
                  children: (
                    <textarea
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white text-sm h-48 font-mono"
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder="메일 본문을 입력하세요 (HTML 태그 사용 가능)"
                    />
                  ),
                },
                {
                  key: 'preview',
                  label: '미리보기',
                  children: (
                    <div
                      className="border border-gray-200 rounded-lg bg-white p-4 max-h-96 overflow-auto"
                      dangerouslySetInnerHTML={{ __html: emailBody || '' }}
                    />
                  ),
                },
              ]}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <AntButton onClick={() => setEmailModalOpen(false)} size="large">
              취소
            </AntButton>
            <AntButton
              type="primary"
              onClick={handleSendEmail}
              loading={sendingEmail}
              disabled={!smtpSettings}
              size="large"
              style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
              icon={<MailOutlined />}
            >
              발송하기
            </AntButton>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SettlementModal;
